import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Briefcase } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import { toast } from 'sonner';

const Internships = () => {
  const [settings, setSettings] = useState(null);
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, careersRes] = await Promise.all([
        api.get('/internship/settings'),
        api.get('/careers?active_only=true&internships_only=true')
      ]);
      setSettings(settingsRes.data);
      setInternships(careersRes.data);
    } catch (error) {
      toast.error('Failed to load internship information');
    } finally {
      setLoading(false);
    }
  };

 if (loading) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          
          {/* Spinner */}
          <div className="w-14 h-14 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />

          {/* Brand Text */}
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-gradient">
              CodeSky
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Loading Internships...
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}


  if (!settings?.enabled) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-6" />
              <h1 className="text-4xl font-heading font-bold text-slate-900 mb-4">
                Internships Currently Unavailable
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                We're not accepting internship applications at the moment. Please check back later or explore our full-time opportunities.
              </p>
              <Link to="/careers" className="bg-primary text-white px-8 py-4 rounded-full font-medium hover:shadow-xl hover:scale-105 transition-all duration-300 inline-block">
                View Careers
              </Link>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50/30" data-testid="internships-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <h1 className="text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6">
              {settings.title}
            </h1>
            {settings.description && (
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                {settings.description}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          {internships.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-600 text-lg">No internship positions available at the moment.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {internships.map((internship, index) => (
                <motion.div
                  key={internship.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all duration-300"
                  data-testid={`internship-card-${index}`}
                >
                  <h3 className="text-2xl font-heading font-bold text-slate-900 mb-3">{internship.title}</h3>
                  <p className="text-slate-600 mb-4">{internship.description}</p>
                  <Link
                    to="/careers"
                    className="inline-block bg-primary text-white px-6 py-2 rounded-full font-medium hover:shadow-lg transition-all"
                  >
                    Apply Now
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Internships;
